import React from "react";
import { Tooltip } from "@mui/material";
import { useUser } from "src/business-types/grocery/components/UserContext";
import { colorOptions, textColorOptions, fontOptions } from "../../profile/theme/themeConstants";

interface MenuItemProps {
  title: string;
  path?: string;
  Icon: React.ElementType;
  compact: boolean;
  isActive: boolean;
  onClick: (title: string, path?: string) => void;
}

const MenuItem: React.FC<MenuItemProps> = ({ title, path, Icon, compact, isActive, onClick,}) => {
  const { user } = useUser();

  const themeKey = user?.background_colour || "DodgerBlue";
  const bgClass = colorOptions[themeKey]?.split(" ")[0];
  const textClass = textColorOptions[themeKey];
  const fontClass = fontOptions[user?.font_family || "Sans-serif"];

  if (!path) return null;

  const handleClick = () => onClick(title, path);

  const baseClasses = `flex items-center rounded-xl transition-all duration-300 ease-in-out cursor-pointer`;
  const activeClasses = `${bgClass} text-white shadow-md border border-white translate-x-0.5`;
  const inactiveClasses = `bg-transparent ${textClass} hover:bg-gray-100/20 hover:translate-x-1`;

  return compact ? (
    <Tooltip title={title} placement="right" arrow>
      <div
        onClick={handleClick}
        className={`flex items-center justify-center w-[60px] h-[60px] rounded-xl transition-all duration-300 ease-in-out cursor-pointer ${
          isActive ? activeClasses : inactiveClasses
        }`}
      >
        <Icon
          className={`text-2xl ${isActive ? "text-white" : textClass} transition-colors`}
        />
      </div>
    </Tooltip>
  ) : (
    <div
      onClick={handleClick}
      className={`${baseClasses} w-full max-w-[140px] h-[60px] px-2 mb-3 gap-2 overflow-hidden ${isActive ? activeClasses : inactiveClasses} ${fontClass}`}
    >
      {/* ✅ Icon (fixed width, always centered vertically) */}
      <div className="flex-shrink-0 w-6 flex items-center justify-center">
        <Icon
          className={`text-2xl ${isActive ? "text-white" : textClass} transition-colors`}
        />
      </div>

      {/* ✅ Title (truncated, perfectly aligned) */}
      <p
        className={`text-sm font-semibold truncate ${isActive ? "text-white" : textClass}`}
        title={title} // hover tooltip for long names
      >
        {title}
      </p>
    </div>
  );
};

export default MenuItem;
